import * as React from 'react';
import * as Images from 'pos/Images';

import { Command } from 'pos/common';

import { RestaurantFacade } from 'pos/facades';
import { ActionButton, BackActionButton, Content, Filler, Footer, Header, LargeDeviceOnly, PhoneOnly, PosScreen } from 'pos/ui';
import { Preconditions } from 'core/infrastructure-next';
import { OfferInListDTO, Res, Permission } from 'core/api';
import { selectTicketOffer } from './selectTicketOffer';
import * as SalesStore from '../SalesStore';
import { CustomerDisplay } from 'pos/services';
import { Message, Modal } from 'core/ui-next/modal';
import { HStack, VStack, Box, Table, Text, TableScrollArea } from '@chakra-ui/react';
import { BasicButton, SvgIcon } from 'core/ui-next';
import { isPhone } from 'pos/ui/ThemeManager';


type Props = {
	offers: OfferInListDTO[];
	initialAppliedOffers: OfferInListDTO[];
} & Modal.Actions<void>;

// Alto máximo de la tabla de promociones en TPV/desktop. La tarjeta crece con su contenido hasta este alto y, a partir
// de ahí, es la propia tabla la que hace scroll (igual que el panel de promociones de Crear/Editar Cliente).
const OFFERS_TABLE_MAX_HEIGHT = '96';

// La promoción seleccionada es siempre un elemento del propio array de promociones aplicadas, pero la comparamos por Id
// igual que hacía la antigua clase, porque el listado de promociones disponibles puede traer otras instancias del DTO.
const isSelected = (offer: OfferInListDTO, selectedOffer: Nullable<OfferInListDTO>) =>
	selectedOffer !== null && offer.Id === selectedOffer.Id;

const selectedOfferBackground = (offer: OfferInListDTO, selectedOffer: Nullable<OfferInListDTO>) =>
	isSelected(offer, selectedOffer) ? { base: '{colors.blue.200}', _dark: '{colors.blue.500}' } : undefined;

// Botón de acción de la cabecera (icono enmarcado + etiqueta debajo), con el mismo estilo que los botones del panel de
// promociones de la pantalla de Crear/Editar Cliente.
function panelActionButton(label: string, icon: React.ComponentProps<typeof SvgIcon>['src'], onClick: () => void, disabled = false): React.ReactNode {
	return (
		<VStack
			width={'12'}
			gapY={'1'}
			cursor={disabled ? 'default' : 'pointer'}
			opacity={disabled ? 0.4 : 1}
			onClick={disabled ? undefined : onClick}
		>
			<Box padding={'1.5'} borderColor={'neutral.300'} borderWidth={'1px'} borderRadius={'md'} bg={{ base: 'white', _dark: 'neutral.900' }}>
				<SvgIcon src={icon} width={'5'} height={'5'} />
			</Box>
			<Text fontSize={'2xs'} textAlign={'center'} lineHeight={'initial'}>{label}</Text>
		</VStack>
	);
}

function SetTicketOffer(props: Props) {

	const { offers, initialAppliedOffers, cancel, close } = props;

	// Estado: las promociones aplicadas al ticket y la que está seleccionada en la tabla. La antigua clase mutaba el
	// array del estado (push / remove) y volvía a hacer setState con la misma referencia; aquí creamos siempre un array
	// nuevo para que React repinte la tabla.
	const [appliedOffers, setAppliedOffers] = React.useState<OfferInListDTO[]>(initialAppliedOffers);
	const [selectedOffer, setSelectedOffer] = React.useState<Nullable<OfferInListDTO>>(null);

	const onSelectOffer = React.useCallback((offer: OfferInListDTO) => setSelectedOffer(offer), []);

	const addOffer = React.useCallback(async () => {

		if (appliedOffers.length === offers.length) {
			await Message.show(Res.Msg.MSG_NOT_NEW_EXISTING_OFFERS());
			return;
		}

		// Sólo se pueden añadir las promociones que todavía no están aplicadas al ticket
		const offersToSelect = offers.filter(offer => !appliedOffers.some(applied => applied.Id === offer.Id));

		const result = await selectTicketOffer(offersToSelect);
		if (!result.ok)
			return;

		setAppliedOffers([...appliedOffers, result.value]);
		setSelectedOffer(result.value);

	}, [appliedOffers, offers]);

	const removeOffer = React.useCallback(async () => {

		if (selectedOffer === null)
			return;

		setAppliedOffers(appliedOffers.filter(offer => offer.Id !== selectedOffer.Id));
		setSelectedOffer(null);

	}, [appliedOffers, selectedOffer]);

	const setOffers = React.useCallback(async () => {
		await SalesStore.setOffers(appliedOffers);
		close();
	}, [appliedOffers, close]);

	const onCancel = React.useCallback(async () => { cancel(); }, [cancel]);

	const hasSelection = selectedOffer !== null;

	return (
		<PosScreen screenId={'SET_TICKET_OFFERS'} size={'medium'} useKeyPad={false}>
			<Header title={Res.Web.SET_TICKET_OFFERS()} image={Images.Offer} onClose={onCancel} />
			<Content>
				<VStack
					width={'full'}
					height={'full'}
					gap={'4'}
					alignItems={'stretch'}
					paddingX={isPhone() ? '4' : '4'}
					paddingY={isPhone() ? '9' : '4'}
					bg={{ base: 'white', _dark: 'neutral.900' }}
				>
					<HStack width={'full'} gap={'2'} alignItems={'center'} justifyContent={isPhone() ? 'center' : 'space-between'}>
						<HStack gap={'2'} alignItems={'center'}>
							<LargeDeviceOnly>
								<SvgIcon src={Images.Offer} width={'6'} height={'6'} />
							</LargeDeviceOnly>
							<Text fontSize={'md'} fontWeight={'semibold'}>{Res.Web.SET_TICKET_OFFERS_CURRENT_OFFERS_CLM()}</Text>
						</HStack>
						{/* En Phone/comandera las acciones son los botones del pie de pantalla, así que aquí sólo se pintan en TPV */}
						<LargeDeviceOnly>
							<HStack gap={'4'} alignItems={'flex-start'}>
								{panelActionButton(Res.Web.SET_CUSTOMER_OFFERS_DELETE_OFFER_BTN(), Images.TrashCan, removeOffer, !hasSelection)}
								{panelActionButton(Res.Web.SET_CUSTOMER_OFFERS_ADD_OFFER_BTN(), Images.Plus, addOffer)}
							</HStack>
						</LargeDeviceOnly>
					</HStack>

					{/* La tarjeta crece con la tabla hasta su alto máximo y a partir de ahí hace scroll vertical */}
					<TableScrollArea
						width={'full'}
						maxH={OFFERS_TABLE_MAX_HEIGHT}
						overflowY={'auto'}
						rounded={'md'}
					>
						<Table.Root size={'sm'} striped interactive stickyHeader>
							<Table.Header>
								<Table.Row>
									<Table.ColumnHeader>
										<Text fontSize={'sm'} textAlign={'start'} fontWeight={'semibold'} color={{ base: '{colors.neutral.800}', _dark: 'white' }}>{Res.Web.CUSTOMER_OFFERS_CLM()}</Text>
									</Table.ColumnHeader>
								</Table.Row>
							</Table.Header>
							<Table.Body>
								{appliedOffers.length === 0
									? <Table.Row>
										<Table.Cell colSpan={1} minH={'5'}>
											<Text fontSize={'sm'} color={{ base: 'neutral.500', _dark: 'neutral.400' }}>{Res.Web.SET_TICKET_OFFERS_INFO_LBL()}</Text>
										</Table.Cell>
									</Table.Row>
									: appliedOffers.map(offer => {
										const background = selectedOfferBackground(offer, selectedOffer);

										return (
											<Table.Row key={offer.Id} onClick={() => onSelectOffer(offer)} cursor={'pointer'}>
												<Table.Cell bg={background}><Text textAlign={'start'} fontSize={'sm'}>{offer.Name}</Text></Table.Cell>
											</Table.Row>
										);
									})}
							</Table.Body>
						</Table.Root>
					</TableScrollArea>
					<HStack width={'full'} justifyContent={'center'} paddingTop={'2'}>
						<HStack width={'80'} height={'10'}>
							<BasicButton type={'success'} text={Res.Web.SET_TICKET_OFFERS_APPLY_BTN()} onClick={setOffers} width={'full'} height={'full'} />
						</HStack>
					</HStack>
				</VStack>
			</Content>
			<Footer>
				<PhoneOnly>
					<BackActionButton action={onCancel} />
					<Filler />
					{/* El ActionButton no tiene estado deshabilitado: si no hay promoción seleccionada el borrado simplemente no hace nada */}
					<ActionButton title={Res.Web.SET_CUSTOMER_OFFERS_DELETE_OFFER_BTN()} image={Images.TrashCan} action={removeOffer} />
					<ActionButton title={Res.Web.SET_CUSTOMER_OFFERS_ADD_OFFER_BTN()} image={Images.Plus} action={addOffer} />
				</PhoneOnly>
			</Footer>
		</PosScreen>
	);
}


declare global {
	interface CommandType {
		setTicketOffers: () => Promise<Result>
	}
}

Command.register('setTicketOffers', Preconditions.and(Preconditions.ensure(Permission.Ticket_ApplyOffer), Preconditions.ensureIsNotLite), async (): Promise<Result> => {

	const ticket = SalesStore.getTicket();
	const offers = await RestaurantFacade.getOffers(ticket.Id);
	const initialOffers: OfferInListDTO[] = [];
	initialOffers.push(...ticket.Offers);
	const result = await Modal.show(SetTicketOffer, { offers, initialAppliedOffers: initialOffers });

	if (result.ok)
		await CustomerDisplay.displayOffers(SalesStore.getTicket());

	return result;
});
